import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { FormRow } from '@/components/Form'
import { ErrorBlock } from '@/components/ErrorBlock'
import { Button } from '@/components/Button'
import {
  NotificationBar,
  NotificationBarSubtitle,
  NotificationBarTitle,
} from '@/components/NotificationBar'
import { byDesign } from '@/infra/design'
import imgJobRatingUrl from '@/assets/misc/img_job-rating.svg'
import RatingsIllustration from '@/components/SpotIllustration/RatingsIllustration'

export interface CloseJobAsAdminDialogProps {
  showing: boolean
  corporateMode: boolean
  jobName: string
  resolutionField: ReactNode
  reasonField: ReactNode
  shouldBillJobField: ReactNode
  billingMethodField: ReactNode
  billingAllowed: boolean
  shouldCollectBillingMethod: boolean
  isScopeChangeRequestPending: boolean
  isCloseJobPending: boolean
  isCloseJobRejected: boolean
  closeJobError?: unknown
  onClose: () => unknown
  onCloseJob: () => unknown
  onDismissError: () => unknown
  submitButtonTestId?: string
}

export function CloseJobAsAdminDialog({
  showing,
  corporateMode,
  jobName,
  resolutionField,
  reasonField,
  shouldBillJobField,
  billingMethodField,
  billingAllowed,
  shouldCollectBillingMethod,
  isScopeChangeRequestPending,
  isCloseJobPending,
  isCloseJobRejected,
  closeJobError,
  onClose,
  onCloseJob,
  onDismissError,
  submitButtonTestId,
}: CloseJobAsAdminDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isCloseJobPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={imgJobRatingUrl}
              illustration={byDesign(undefined, RatingsIllustration)}
              alt="Illustration of a finger tapping a star rating on a phone"
            />
          )}
          <ModalSheetContentTitle>Complete Job</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            By marking this <strong>{jobName}</strong> as completed,
            communication with the client will cease.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <FormRow>{resolutionField}</FormRow>

          <FormRow>{reasonField}</FormRow>

          {billingAllowed && <FormRow>{shouldBillJobField}</FormRow>}

          {shouldCollectBillingMethod && (
            <FormRow>{billingMethodField}</FormRow>
          )}
        </ModalSheetContentBlock>
      </ModalSheetContent>
      {isCloseJobRejected && (
        <ErrorBlock
          tackOn="bottom"
          error={closeJobError}
          onClose={() => onDismissError()}
        />
      )}
      {isScopeChangeRequestPending && <PendingUpdatesNotificationBar />}
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            onPress={onClose}
            isDisabled={isCloseJobPending}
          />
          <Button
            label="Close Job"
            variant="primary"
            onPress={onCloseJob}
            testId={submitButtonTestId}
            isPending={isCloseJobPending}
            isDisabled={isScopeChangeRequestPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

function PendingUpdatesNotificationBar() {
  return (
    <NotificationBar centered type="error">
      <NotificationBarTitle>Pending updates:</NotificationBarTitle>
      <NotificationBarSubtitle>
        Unable to close due to pending job scope changes
      </NotificationBarSubtitle>
    </NotificationBar>
  )
}
